import React, { useState, useEffect, useMemo } from "react";
import ReactECharts from "echarts-for-react";

// Function to generate unique pastel colors
const generateColor = (index) => {
  const baseColors = ["#4BD5CE", "#FEC941", "#6482C3", "#197FC7", "#56B6FC"];
  if (index < baseColors.length) return baseColors[index];

  // Generate unique color variation using HSL manipulation
  const hueShift = (index * 137) % 360;
  return `hsl(${hueShift}, 60%, 65%)`;
};

// Convert a date string to the corresponding financial year
const getFinancialYear = (dateStr) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fyStart = month >= 3 ? year : year - 1;
  return `FY ${fyStart}-${fyStart + 1}`;
};

// Get unique financial years from project list, sorted in descending order
const getFinancialYears = (projects, includePreviousFY = false) => {
  const fySet = new Set();
  let minFYYear = new Date().getFullYear();

  projects.forEach((p) => {
    const date = new Date(p.start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    const fyLabel = `FY ${fyStart}-${fyStart + 1}`;

    fySet.add(fyLabel);
    if (fyStart < minFYYear) {
      minFYYear = fyStart;
    }
  });

  if (includePreviousFY) {
    fySet.add(`FY ${minFYYear - 1}-${minFYYear}`);
  }

  return [...fySet].sort((a, b) => {
    const aStart = +a.split(" ")[1].split("-")[0];
    const bStart = +b.split(" ")[1].split("-")[0];
    return bStart - aStart;
  });
};

const ProjectManagerBarChart = ({ projects }) => {
  const [selectedFY, setSelectedFY] = useState("");

  const financialYears = useMemo(() => getFinancialYears(projects), [projects]);

  useEffect(() => {
    if (financialYears.length > 0) {
      setSelectedFY(financialYears[0]);
    }
  }, [financialYears]);

  // Filter projects by selected FY
  const filteredProjects = selectedFY
    ? projects.filter((p) => getFinancialYear(p.start_date) === selectedFY)
    : projects;

  // Group projects by project manager
  const managerCounts = filteredProjects.reduce((acc, project) => {
    const manager = project.project_manager_names;
    acc[manager] = (acc[manager] || 0) + 1;
    return acc;
  }, {});

  const managers = Object.keys(managerCounts);
  const projectCounts = Object.values(managerCounts);

  const colorMapping = {};
  managers.forEach((manager, index) => {
    colorMapping[manager] = generateColor(index);
  });

  const option = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: "3%", right: "4%", bottom: "8%", containLabel: true },
    xAxis: [
      {
        type: "category",
        data: managers,
        axisTick: { alignWithLabel: true },
        axisLabel: {
          fontFamily: '"Lato", sans-serif',
          fontSize: 13,
          color: "#000",
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          fontFamily: '"Lato", sans-serif',
          fontSize: 13,
          color: "#000",
        },
      },
    ],
    series: [
      {
        name: "Projects",
        type: "bar",
        barWidth: "60%",
        data: projectCounts.map((count, index) => ({
          value: count,
          itemStyle: { color: colorMapping[managers[index]] },
          label: {
            show: true,
            position: "top",
            color: "#000",
            fontSize: 14,
            fontFamily: '"Lato", sans-serif',
          },
        })),
      },
    ],
  };

  return (
    <div style={{ width: "100%" }}>
      <div className="project-bar-status">
        <h2 className="pie-header">Projects by Project Owner</h2>
        <select
          className="pm-fy-dd"
          value={selectedFY}
          onChange={(e) => setSelectedFY(e.target.value)}
        >
          <option value="">All Years</option>
          {financialYears.map((fy) => (
            <option key={fy} value={fy}>
              {fy}
            </option>
          ))}
        </select>

        {/* Financial Year Filter */}

        <p className="bar-chart-text">Number of Projects</p>
        <p className="bar-chart-text-bottom">Project Owner</p>

        <ReactECharts
          className="manager"
          option={option}
          style={{
            height: "420px",
            width: "98%",
            top: "-1rem",
            backgroundColor: "transparent",
          }}
        />
      </div>
    </div>
  );
};

export default ProjectManagerBarChart;
